/**
 * Platform admin tab for managing modules and module groups.
 * Supports drag-and-drop reordering with dnd-kit for hierarchical organization.
 */
import {
	closestCenter,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragOverEvent,
	type DragStartEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { GroupSection, ModuleItem, type GroupData, type ModuleItemData } from "./modules";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";

/**
 * Modules management tab with WYSIWYG drag-and-drop interface
 */
export function ModulesTab() {
	const [, setSearchParams] = useSearchParams();

	// Dialog states
	const [deletingModule, setDeletingModule] = useState<ModuleItemData | null>(null);
	const [createGroupOpen, setCreateGroupOpen] = useState(false);
	const [editingGroup, setEditingGroup] = useState<GroupData | null>(null);
	const [deletingGroup, setDeletingGroup] = useState<GroupData | null>(null);

	// Drag state
	const [activeId, setActiveId] = useState<string | null>(null);
	const [activeType, setActiveType] = useState<"module" | "group" | null>(null);

	// Form state for create group
	const [newGroupName, setNewGroupName] = useState("");
	const [newGroupSeparator, setNewGroupSeparator] = useState(false);

	// Form state for edit group
	const [editGroupName, setEditGroupName] = useState("");
	const [editGroupSeparator, setEditGroupSeparator] = useState(false);

	const utils = trpc.useUtils();

	// Query for all modules and groups
	const listAllQuery = trpc.modules.core.listAll.useQuery();

	// DnD sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	// Module mutations
	const deleteModuleMutation = trpc.modules.core.delete.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
			setDeletingModule(null);
		},
	});

	const toggleEnabledMutation = trpc.modules.core.toggleEnabled.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
		},
	});

	const reorderModulesMutation = trpc.modules.core.reorder.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
		},
	});

	const reorderGroupsMutation = trpc.modules.core.reorderGroups.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
		},
	});

	// Group mutations
	const createGroupMutation = trpc.modules.core.createGroup.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
			setCreateGroupOpen(false);
			resetGroupForm();
		},
	});

	const updateGroupMutation = trpc.modules.core.updateGroup.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
			setEditingGroup(null);
		},
	});

	const deleteGroupMutation = trpc.modules.core.deleteGroup.useMutation({
		onSuccess: () => {
			void utils.modules.core.listAll.invalidate();
			void utils.modules.list.invalidate();
			setDeletingGroup(null);
		},
	});

	// Computed data
	const { modules, groups, ungroupedModules, groupedModules } = useMemo(() => {
		const mods = listAllQuery.data?.modules ?? [];
		const grps = listAllQuery.data?.groups ?? [];
		const ungrouped = mods.filter((m) => !m.groupId).sort((a, b) => a.order - b.order);
		const grouped = grps.map((g) => ({
			...g,
			modules: mods.filter((m) => m.groupId === g.id).sort((a, b) => a.order - b.order),
		}));
		return { modules: mods, groups: grps, ungroupedModules: ungrouped, groupedModules: grouped };
	}, [listAllQuery.data]);

	// Get the active item for drag overlay
	const activeModule = activeType === "module" ? modules.find((m) => m.id === activeId) : null;
	const activeGroup = activeType === "group" ? groups.find((g) => `group-${g.id}` === activeId) : null;

	/**
	 * Resets the group creation form to initial state
	 */
	const resetGroupForm = () => {
		setNewGroupName("");
		setNewGroupSeparator(false);
	};

	/**
	 * Deletes the module marked for deletion
	 */
	const handleDeleteModule = () => {
		if (!deletingModule) return;
		deleteModuleMutation.mutate({ moduleId: deletingModule.id });
	};

	/**
	 * Toggles a module's enabled state
	 * @param module - the module to toggle
	 */
	const handleToggleEnabled = (module: ModuleItemData) => {
		toggleEnabledMutation.mutate({
			moduleId: module.id,
			enabled: !module.enabled,
		});
	};

	/**
	 * Opens the module editor drawer
	 * @param module - the module to edit
	 */
	const handleOpenEditor = (module: ModuleItemData) => {
		setSearchParams({ drawer: "module", id: module.id });
	};

	/**
	 * Creates a new module group
	 */
	const handleCreateGroup = () => {
		// Calculate order: add to end
		const maxOrder = groups.length > 0 ? Math.max(...groups.map((g) => g.order)) + 1 : 0;

		createGroupMutation.mutate({
			name: newGroupName || null,
			order: maxOrder,
			separatorAfter: newGroupSeparator,
		});
	};

	/**
	 * Opens the edit dialog for a group
	 * @param group - the group to edit
	 */
	const handleEditGroup = (group: GroupData) => {
		setEditingGroup(group);
		setEditGroupName(group.name ?? "");
		setEditGroupSeparator(group.separatorAfter);
	};

	/**
	 * Updates the currently editing group
	 */
	const handleUpdateGroup = () => {
		if (!editingGroup) return;
		updateGroupMutation.mutate({
			groupId: editingGroup.id,
			name: editGroupName || null,
			separatorAfter: editGroupSeparator,
		});
	};

	/**
	 * Deletes the group marked for deletion
	 */
	const handleDeleteGroup = () => {
		if (!deletingGroup) return;
		deleteGroupMutation.mutate({ groupId: deletingGroup.id });
	};

	/**
	 * Handles drag start event for modules and groups
	 * @param event - the drag start event
	 */
	const handleDragStart = (event: DragStartEvent) => {
		const id = event.active.id.toString();
		if (id.startsWith("group-")) {
			setActiveId(id);
			setActiveType("group");
		}
		else {
			setActiveId(id);
			setActiveType("module");
		}
	};

	/**
	 * Handles drag over event for visual feedback
	 * @param _event - the drag over event
	 */
	const handleDragOver = (_event: DragOverEvent) => {
		// Optional: could add visual feedback here
	};

	/**
	 * Handles drag end event to reorder modules and groups
	 * @param event - the drag end event
	 */
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		setActiveId(null);
		setActiveType(null);

		if (!over || active.id === over.id) return;

		const activeIdStr = active.id.toString();
		const overIdStr = over.id.toString();

		// Check if dragging a group
		if (activeIdStr.startsWith("group-")) {
			// Reordering groups
			const activeGroupId = activeIdStr.replace("group-", "");
			const overGroupId = overIdStr.replace("group-", "");

			const activeIndex = groups.findIndex((g) => g.id === activeGroupId);
			const overIndex = groups.findIndex((g) => g.id === overGroupId);

			if (activeIndex !== -1 && overIndex !== -1) {
				const reorderedGroups = arrayMove(groups, activeIndex, overIndex);
				const groupOrders = reorderedGroups.map((g, idx) => ({
					groupId: g.id,
					order: idx,
					separatorAfter: g.separatorAfter,
				}));
				reorderGroupsMutation.mutate({ groupOrders });
			}
			return;
		}

		// Dragging a module
		const draggedModule = modules.find((m) => m.id === activeIdStr);
		if (!draggedModule) return;

		// Determine target: another module or a droppable zone
		let targetGroupId: string | null = null;
		let targetIndex = -1;

		if (overIdStr.startsWith("droppable-")) {
			// Dropped onto a group's droppable zone
			targetGroupId = overIdStr.replace("droppable-", "");
			// Add to end of that group
			const groupModules = modules.filter((m) => m.groupId === targetGroupId);
			targetIndex = groupModules.length;
		}
		else if (overIdStr === "droppable-ungrouped") {
			// Dropped onto ungrouped zone
			targetGroupId = null;
			targetIndex = ungroupedModules.length;
		}
		else {
			// Dropped onto another module
			const targetModule = modules.find((m) => m.id === overIdStr);
			if (!targetModule) return;

			targetGroupId = targetModule.groupId;
			const relevantModules = targetGroupId
				? modules.filter((m) => m.groupId === targetGroupId).sort((a, b) => a.order - b.order)
				: ungroupedModules;
			targetIndex = relevantModules.findIndex((m) => m.id === overIdStr);
		}

		// Build the new order for affected modules
		const sourceGroupId = draggedModule.groupId;
		const isSameGroup = sourceGroupId === targetGroupId;

		if (isSameGroup) {
			// Reordering within the same group
			const relevantModules = targetGroupId
				? modules.filter((m) => m.groupId === targetGroupId).sort((a, b) => a.order - b.order)
				: [...ungroupedModules];

			const oldIndex = relevantModules.findIndex((m) => m.id === draggedModule.id);
			if (oldIndex !== -1 && targetIndex !== -1 && oldIndex !== targetIndex) {
				const reordered = arrayMove(relevantModules, oldIndex, targetIndex);
				const moduleOrders = reordered.map((m, idx) => ({
					moduleId: m.id,
					order: idx,
					groupId: m.groupId,
				}));
				reorderModulesMutation.mutate({ moduleOrders });
			}
		}
		else {
			// Moving to a different group
			// Remove from source group and add to target group
			const sourceModules = sourceGroupId
				? modules.filter((m) => m.groupId === sourceGroupId && m.id !== draggedModule.id).sort((a, b) => a.order - b.order)
				: ungroupedModules.filter((m) => m.id !== draggedModule.id);

			const targetModules = targetGroupId
				? modules.filter((m) => m.groupId === targetGroupId).sort((a, b) => a.order - b.order)
				: [...ungroupedModules.filter((m) => m.id !== draggedModule.id)];

			// Insert at target position
			const insertIndex = targetIndex >= 0 ? targetIndex : targetModules.length;
			targetModules.splice(insertIndex, 0, { ...draggedModule, groupId: targetGroupId });

			// Build update for all affected modules
			const moduleOrders: { moduleId: string; order: number; groupId: string | null }[] = [];

			// Source group reorder
			sourceModules.forEach((m, idx) => {
				moduleOrders.push({ moduleId: m.id, order: idx, groupId: m.groupId });
			});

			// Target group reorder (including moved module)
			targetModules.forEach((m, idx) => {
				moduleOrders.push({ moduleId: m.id, order: idx, groupId: targetGroupId });
			});

			reorderModulesMutation.mutate({ moduleOrders });
		}
	};

	// IDs for sortable contexts
	const ungroupedModuleIds = ungroupedModules.map((m) => m.id);
	const groupIds = groups.map((g) => `group-${g.id}`);

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">Module Layout</h2>
					<p className="text-sm text-muted-foreground">
						Drag modules and groups to reorder. This preview matches the sidebar layout.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" onClick={() => { setCreateGroupOpen(true); }}>
						Add Group
					</Button>
					<Button onClick={() => { setSearchParams({ drawer: "module" }); }}>
						Add Module
					</Button>
				</div>
			</div>

			{/* Loading State */}
			{listAllQuery.isLoading && (
				<div className="rounded-lg border p-8 text-center text-muted-foreground">
					Loading modules...
				</div>
			)}

			{/* Error State */}
			{listAllQuery.error && (
				<div className="rounded-lg border p-8 text-center text-red-400">
					Error loading modules: {listAllQuery.error.message}
				</div>
			)}

			{/* DnD Context */}
			{listAllQuery.data && (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragStart={handleDragStart}
					onDragOver={handleDragOver}
					onDragEnd={handleDragEnd}
				>
					<div className="space-y-4">
						{/* Ungrouped Modules Section */}
						<div className="rounded-lg border bg-card/50">
							<div className="border-b px-3 py-2">
								<div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
									Ungrouped Modules
								</div>
							</div>
							<div className="min-h-[60px] space-y-2 p-3">
								<SortableContext items={ungroupedModuleIds} strategy={verticalListSortingStrategy}>
									{ungroupedModules.map((module) => (
										<ModuleItem
											key={module.id}
											module={module}
											onEdit={handleOpenEditor}
											onDelete={setDeletingModule}
											onToggleEnabled={handleToggleEnabled}
											onOpenEditor={handleOpenEditor}
										/>
									))}
								</SortableContext>
								{ungroupedModules.length === 0 && (
									<div className="flex h-[60px] items-center justify-center rounded-lg border-2 border-dashed text-sm text-muted-foreground">
										No ungrouped modules
									</div>
								)}
							</div>
						</div>

						{/* Groups */}
						<SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
							{groupedModules.map((group) => (
								<GroupSection
									key={group.id}
									group={group}
									moduleIds={group.modules.map((m) => m.id)}
									onEdit={handleEditGroup}
									onDelete={setDeletingGroup}
								>
									{group.modules.map((module) => (
										<ModuleItem
											key={module.id}
											module={module}
											onEdit={handleOpenEditor}
											onDelete={setDeletingModule}
											onToggleEnabled={handleToggleEnabled}
											onOpenEditor={handleOpenEditor}
										/>
									))}
								</GroupSection>
							))}
						</SortableContext>
					</div>

					{/* Drag Overlay */}
					<DragOverlay>
						{activeModule && (
							<div className="rounded-lg border bg-card px-3 py-2 shadow-lg opacity-90">
								<div className="text-sm font-bold">{activeModule.name}</div>
							</div>
						)}
						{activeGroup && (
							<div className="rounded-lg border bg-muted px-3 py-2 shadow-lg opacity-90">
								<div className="text-xs font-medium uppercase">
									{activeGroup.name ?? "(Unnamed Group)"}
								</div>
							</div>
						)}
					</DragOverlay>
				</DndContext>
			)}

			{/* Delete Module Confirmation Dialog */}
			<Dialog open={deletingModule !== null} onOpenChange={(open) => { if (!open) setDeletingModule(null); }}>
				<DialogContent>
					<form onSubmit={(e) => { e.preventDefault(); handleDeleteModule(); }}>
						<DialogHeader>
							<DialogTitle>Delete Module</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete the module &quot;{deletingModule?.name}&quot;? This will also
								remove all user access permissions for this module. This action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						{deleteModuleMutation.error && (
							<p className="text-sm text-red-400">{deleteModuleMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => { setDeletingModule(null); }}>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="destructive"
								disabled={deleteModuleMutation.isPending}
							>
								{deleteModuleMutation.isPending ? "Deleting..." : "Delete Module"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Create Group Dialog */}
			<Dialog open={createGroupOpen} onOpenChange={setCreateGroupOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add Module Group</DialogTitle>
						<DialogDescription>
							Create a new group to organize modules in the sidebar.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={(e) => { e.preventDefault(); handleCreateGroup(); }}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="group-name">Name (optional)</Label>
								<Input
									id="group-name"
									placeholder="Analytics"
									value={newGroupName}
									onChange={(e) => { setNewGroupName(e.target.value); }}
								/>
								<p className="text-xs text-muted-foreground">
									Leave empty for a visual grouping without a header
								</p>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="group-separator"
									checked={newGroupSeparator}
									onCheckedChange={(checked: boolean) => { setNewGroupSeparator(checked); }}
								/>
								<Label htmlFor="group-separator">Show separator line after this group</Label>
							</div>
						</div>
						{createGroupMutation.error && (
							<p className="text-sm text-red-400">{createGroupMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								type="button"
								onClick={() => {
									setCreateGroupOpen(false);
									resetGroupForm();
								}}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={createGroupMutation.isPending}>
								{createGroupMutation.isPending ? "Creating..." : "Create Group"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Edit Group Dialog */}
			<Dialog open={editingGroup !== null} onOpenChange={(open) => { if (!open) setEditingGroup(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit Module Group</DialogTitle>
						<DialogDescription>Update group configuration.</DialogDescription>
					</DialogHeader>
					<form onSubmit={(e) => { e.preventDefault(); handleUpdateGroup(); }}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="edit-group-name">Name (optional)</Label>
								<Input
									id="edit-group-name"
									placeholder="Analytics"
									value={editGroupName}
									onChange={(e) => { setEditGroupName(e.target.value); }}
								/>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="edit-group-separator"
									checked={editGroupSeparator}
									onCheckedChange={(checked: boolean) => { setEditGroupSeparator(checked); }}
								/>
								<Label htmlFor="edit-group-separator">Show separator line after this group</Label>
							</div>
						</div>
						{updateGroupMutation.error && (
							<p className="text-sm text-red-400">{updateGroupMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button variant="outline" type="button" onClick={() => { setEditingGroup(null); }}>
								Cancel
							</Button>
							<Button type="submit" disabled={updateGroupMutation.isPending}>
								{updateGroupMutation.isPending ? "Saving..." : "Save Changes"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Group Confirmation Dialog */}
			<Dialog open={deletingGroup !== null} onOpenChange={(open) => { if (!open) setDeletingGroup(null); }}>
				<DialogContent>
					<form onSubmit={(e) => { e.preventDefault(); handleDeleteGroup(); }}>
						<DialogHeader>
							<DialogTitle>Delete Module Group</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete the group &quot;{deletingGroup?.name ?? "(No name)"}&quot;?
								Modules in this group will become ungrouped. This action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						{deleteGroupMutation.error && (
							<p className="text-sm text-red-400">{deleteGroupMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => { setDeletingGroup(null); }}>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="destructive"
								disabled={deleteGroupMutation.isPending}
							>
								{deleteGroupMutation.isPending ? "Deleting..." : "Delete Group"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
